import { Context } from '#store/overmind';
import { UserProfile } from '#types';
import { logger } from '#utils';
import Router from 'next/router';
import { deleteCookie } from 'cookies-next';
import { getApolloClient } from '#providers/apollo/initializeClient';

export const loadUser = async ({ state, effects, actions }: Context) => {
  state.user.loading = true;

  try {
    const response = await effects.user.api.getUserDetails();
    const responseData = response.data;
    const { extra, userProfiles, pinSettingsStatus } = responseData;
    const profileId = parseInt(localStorage.getItem('profileId'), 10);
    state.user.extra = extra ?? {};
    state.user.pinSettingsStatus = pinSettingsStatus ?? 'not_configured';
    state.user.userProfiles = userProfiles ?? [];
    const currentProfile = userProfiles?.find(
      (userProfile) => userProfile.id === profileId
    );
    actions.user.setActiveProfile(currentProfile);
    state.user.loading = false;
  } catch (e) {
    logger.warn('Error on loading user details', { message: e.message });
    state.user.loading = false;
  }
};

export const initProfileData = ({ state, actions }: Context) => {
  const profile = state.user.profile;
  if (profile && profile.id) {
    actions.favourites.loadFavourites();
    actions.continueWatching.loadWatchList();
  }
};

export const setActiveProfile = (
  { state, actions }: Context,
  profile: UserProfile
) => {
  if (state.user?.profile?.id !== profile.id) {
    state.user.profile = { ...profile };
    localStorage.setItem('profileId', String(profile.id));
    actions.user.initProfileData();
  }
  actions.app.hideProfileSelection();
};

export const createProfile = async (
  { state, effects }: Context,
  profile: Pick<UserProfile, 'avatarAssetId' | 'name'>
) => {
  const response = await effects.user.api.createProfile(profile);

  if (response && response.data) {
    const newProfile: UserProfile = response.data;
    state.user.userProfiles = [...state.user.userProfiles, newProfile];
  }
};

export const updateProfile = (
  { state, effects }: Context,
  data: Pick<UserProfile, 'id' | 'name' | 'avatarAssetId'>
) => {
  const profile = state.user.userProfiles.find((entry) => entry.id === data.id);
  const newProfileData = { ...profile, ...data };
  state.user.profile = { ...profile, ...data };
  state.user.userProfiles = state.user.userProfiles.map((entry) => {
    if (entry.id === data.id) {
      return newProfileData;
    }
    return entry;
  });
  effects.user.api.updateProfile(data);
};

export const deleteProfile = (
  { state, effects }: Context,
  profileId: number
) => {
  state.user.userProfiles = state.user.userProfiles.filter(
    (entry: UserProfile) => entry.id !== profileId
  );
  if (state.user.profile?.id === profileId) {
    state.user.profile = state.user.userProfiles[0];
  }
  effects.user.api.deleteProfile(profileId);
};

export const disableParentalControl = async ({ state, effects }: Context) => {
  try {
    const response = await effects.user.api.disableParentalControl();
    const { pinSettingsStatus } = response.data;
    state.user.pinSettingsStatus = pinSettingsStatus ?? 'not_configured';
  } catch (e) {
    logger.warn('Could not disable parental control', { message: e.message });
  }
};

export const resetParentalControl = async ({ state, effects }: Context) => {
  try {
    const response = await effects.user.api.resetParentalControl();
    const responseData = response.data;
    const { pinSettingsStatus } = responseData;
    state.user.pinSettingsStatus = pinSettingsStatus ?? 'not_configured';
  } catch (e) {
    logger.warn('Could not reset parental control', { message: e.message });
  }
};

export const logout = async ({ state, actions, effects }: Context) => {
  await effects.user.api
    .logout()
    .then(async () => {
      await actions.session.deleteSession();
      state.user.extra = null;
      state.user.profile = null;
      state.user.userProfiles = [];
      state.user.isLoggedIn = false;
      state.user.user = null;
      state.continueWatching.items = [];
      localStorage.removeItem('profileId');
      deleteCookie('orderId');

      const client = getApolloClient();
      await client.clearStore();
      await Router.push('/');
    })
    .catch(() => {
      Router.push('/');
    });
};
